import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useApolloClient } from '@apollo/client';
import { Button, Divider, message, Modal, Space } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { type AudiCalculationModuleSpecsFragment } from '../../../../../api/fragments/AudiCalculationModuleSpecs';
import {
    DeleteAudiCalculationModuleSettingDocument,
    type DeleteAudiCalculationModuleSettingMutation,
    type DeleteAudiCalculationModuleSettingMutationVariables,
} from '../../../../../api/mutations/deleteAudiCalculationModuleSetting';

export type AudiCalculationSettingActionsProps = {
    module: AudiCalculationModuleSpecsFragment;
    setting: AudiCalculationModuleSpecsFragment['audiCalculationSettings'][number];
    showUpdate: boolean;
};

const AudiCalculationSettingsAction = ({ module, setting, showUpdate }: AudiCalculationSettingActionsProps) => {
    const moduleId = module.id;
    const audiCalculationSettingId = setting.id;

    const apolloClient = useApolloClient();
    const navigate = useNavigate();
    const { t } = useTranslation(['audiCalculationModuleDetails', 'moduleDetails']);

    const deleteAction = useCallback(() => {
        Modal.confirm({
            title: t('audiCalculationModuleDetails:settings.deleteModal.title'),
            icon: <ExclamationCircleOutlined />,
            content: t('audiCalculationModuleDetails:settings.deleteModal.content'),
            okText: t('audiCalculationModuleDetails:settings.deleteModal.submitButton'),
            okType: 'danger',
            cancelText: t('audiCalculationModuleDetails:settings.deleteModal.cancelButton'),
            onOk: async () => {
                message.loading({
                    content: t('audiCalculationModuleDetails:settings.deleteModal.messages.submitting'),
                    key: 'primary',
                    duration: 0,
                });

                await apolloClient
                    .mutate<
                        DeleteAudiCalculationModuleSettingMutation,
                        DeleteAudiCalculationModuleSettingMutationVariables
                    >({
                        mutation: DeleteAudiCalculationModuleSettingDocument,
                        variables: { moduleId, settingId: audiCalculationSettingId },
                    })
                    .finally(() => {
                        message.destroy('primary');
                    });

                message.success({
                    content: t('audiCalculationModuleDetails:settings.deleteModal.messages.success'),
                    key: 'primary',
                });
            },
        });
    }, [apolloClient, moduleId, audiCalculationSettingId, t]);

    return (
        <Space size="small" split={<Divider type="vertical" />}>
            {showUpdate && (
                <Button
                    onClick={() =>
                        navigate(`/admin/system/modules/${moduleId}/audiCalculation/${audiCalculationSettingId}`)
                    }
                    size="small"
                    style={{ padding: 0 }}
                    type="link"
                >
                    {t('audiCalculationModuleDetails:actions.update')}
                </Button>
            )}
            <Button onClick={deleteAction} size="small" style={{ padding: 0 }} type="link">
                {t('audiCalculationModuleDetails:actions.delete')}
            </Button>
        </Space>
    );
};

export default AudiCalculationSettingsAction;
